import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Box, capitalize, Stack } from '@mui/material'
import { Link } from 'react-router-dom'

import { useStores } from '@trivie/core'
import { Panel } from '@trivie/ui-web/components/panel/panel'
import { Text } from '@trivie/ui-web/components/text'
import { Avatar } from '@trivie/ui-web/components/avatar'
import { color } from '@trivie/ui-web/theme'

import { SubscriptionChip } from '../subscription-chip'
import { SubscriptionTagsPanel } from '../subscription-tags-panel'
import { Instance } from 'mobx-state-tree'
import { PurchaseAttemptType } from '@trivie/core/src/models/subscriptions'
import { DateTime } from 'luxon'

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return 'None'
  return DateTime.fromISO(dateStr).toLocaleString(DateTime.DATETIME_MED)
}

const DetailRow = ({
  label,
  value,
  href,
}: {
  label: string
  value: React.ReactNode
  href?: string
}) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" py={0.5}>
    <Text variant="body1" text={label} sx={{ color: 'text.secondary' }} mr={4} />
    {value === 'Gift' ? (
      <SubscriptionChip status="gifted" />
    ) : href ? (
      <Link
        to={href}
        style={{ textDecoration: 'none', color: '#000', textDecorationLine: 'underline' }}
      >
        <Text variant="body1Medium" text={value as string} sx={{ color: '#000' }} align="right" />
      </Link>
    ) : (
      <Text
        variant="body1Medium"
        text={typeof value === 'string' ? value : ''}
        component={typeof value === 'string' ? undefined : 'div'}
        align="right"
      >
        {typeof value !== 'string' ? value : undefined}
      </Text>
    )}
  </Stack>
)

const Overview = observer(({ id }: { id: string }) => {
  const { subscriptionStore } = useStores()
  const purchase = subscriptionStore.selectedPurchase
  const attempts = subscriptionStore.sortedAttempts

  useEffect(() => {
    if (purchase) {
      subscriptionStore.getPurchaseAttempts(purchase.user_id, Number(id))
    }
  }, [])

  if (!purchase) return null

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
        gap: 2,
        pb: '40px',
      }}
    >
      <Panel title="Purchase Details" fill>
        <Stack gap={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" py={0.5}>
            <Text variant="body1" text={'User'} sx={{ color: 'text.secondary' }} />
            <Link
              to={`/people/users/${purchase.user_id}/overview`}
              style={{ textDecoration: 'none', color: '#000', textDecorationLine: 'none' }}
            >
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Stack direction="column">
                  {purchase.user && (
                    <Text
                      variant="body1Medium"
                      textAlign="end"
                      text={purchase.user}
                      sx={{ color: '#000' }}
                    />
                  )}
                  <Text
                    variant={!purchase.user ? 'body1Medium' : 'body2'}
                    textAlign="end"
                    text={purchase.customer_email}
                    sx={{ color: purchase.user ? color.shade50 : '#000' }}
                  />
                </Stack>
                <Avatar size={32} name={purchase.user} url={''} />
              </Stack>
            </Link>
          </Stack>
          <DetailRow
            label="Subscription"
            value={purchase.product_title}
            href={`/activities/subscriptions/${purchase.product_id}/overview`}
          />
          <DetailRow label="Purchase Date" value={purchase.purchaseDateDisplay || 'N/A'} />
          <DetailRow label="Customer ID" value={purchase.customer_id || 'N/A'} />
          <DetailRow label="Transaction ID" value={purchase.transaction_id} />
          <DetailRow label="Transaction Type" value={capitalize(purchase.transaction_type)} />
          <DetailRow label="Source" value={capitalize(purchase.sourceDisplay)} />
          <DetailRow label="Expiration" value={formatDate(purchase.expiration_date)} />
          <DetailRow label="Receipt" value={purchase.receipt ?? 'N/A'} />
        </Stack>
      </Panel>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
        }}
      >
        <Box sx={{ gridColumn: { lg: '1 / -1' } }}>
          <Panel title="Purchase Attempts" badge={attempts.length} fill>
            {attempts.length === 0 ? (
              <Text
                component="div"
                variant="body1"
                text="No attempts recorded"
                sx={{
                  color: color.shade50,
                  fontStyle: 'italic',
                  display: 'flex',
                  flex: 1,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              />
            ) : (
              <Stack gap={1}>
                {attempts.map((attempt: Instance<PurchaseAttemptType>, idx: number) => (
                  <Stack
                    key={idx}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ p: 1.5, px: 0, borderRadius: 2 }}
                  >
                    <Stack gap={0.25}>
                      <Text variant="body2Medium" text={attempt.details} />
                      <Text
                        variant="caption"
                        text={formatDate(attempt.attempt_timestamp)}
                        sx={{ color: 'text.secondary' }}
                      />
                    </Stack>
                    <SubscriptionChip status={attempt.status} />
                  </Stack>
                ))}
              </Stack>
            )}
          </Panel>
        </Box>
        <Box sx={{ gridColumn: { lg: '1 / -1' } }}>
          <SubscriptionTagsPanel tagIds={purchase?.tag_ids ? purchase.tag_ids : []} />
        </Box>
      </Box>
    </Box>
  )
})

export const SubscriptionPurchaseOverviewTab = (props: any) => {
  const { id } = props
  return {
    name: 'Overview',
    path: `/activities/subscriptions/purchases/${id}/overview`,
    component: () => <Overview id={id} />,
  }
}
